import { KeyValue } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    InputSignal,
    model,
    ModelSignal,
    output,
    OutputEmitterRef,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subscription } from 'rxjs';

import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import {
    ButtonComponent,
    ButtonType,
    IconPosition,
} from '@connect-services/src/lib/components/button/button.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { RessourcesModule } from '@ressources/client/ressources.module';

export interface SearchParams {
    showAll: boolean;
    value: string | null;
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, InputTextComponent, NoDataToDisplayComponent, ReactiveFormsModule, RessourcesModule],
    selector: 'app-deposit-layer-filter-form',
    templateUrl: './deposit-layer-filter-form.component.html',
})
export class DepositLayerFilterFormComponent {
    public readonly control: ModelSignal<FormControl<string[]>> = model.required<FormControl<string[]>>();
    public readonly displayShowMore: InputSignal<boolean> = input.required<boolean>();
    public readonly editDisabled: InputSignal<boolean> = input<boolean>(false);
    public readonly paneWidthTwoThird: InputSignal<boolean> = input.required<boolean>();
    public readonly reset: InputSignal<number> = input.required<number>();
    public search: OutputEmitterRef<SearchParams> = output<SearchParams>();
    public readonly searchLabel: InputSignal<string> = input.required<string>();
    public toggleShowAll = output<boolean>();
    public readonly totalCount: InputSignal<number> = input.required<number>();
    public readonly values: InputSignal<KeyValue<string, string>[]> = input.required<KeyValue<string, string>[]>();

    protected readonly ButtonType = ButtonType;
    protected readonly IconPosition = IconPosition;
    protected readonly searchControl: FormControl<string | null> = new FormControl('');

    protected readonly showAll = computed(() => this.values().length === this.totalCount());
    private destroyRef = inject(DestroyRef);
    private searchControlSubscription: Subscription | undefined;

    public constructor() {
        this.searchControlSubscription = this.searchControl.valueChanges
            .pipe(debounceTime(500), distinctUntilChanged())
            .subscribe((value) =>
                this.search.emit({
                    showAll: this.showAll(),
                    value,
                }),
            );

        this.destroyRef.onDestroy(() => this.searchControlSubscription?.unsubscribe());

        effect(() => {
            if (this.editDisabled()) {
                this.searchControl.disable();

                return;
            }

            this.searchControl.enable();
        });

        effect(() => {
            if (this.reset()) {
                this.searchControl.setValue('');
            }
        });
    }

    protected selectValue(value: KeyValue<string, string>): void {
        this.control().markAsDirty();
        const currentIds = this.control().value;
        if (currentIds.includes(value.value)) {
            this.control().setValue(currentIds.filter((id: string) => id !== value.value));

            return;
        }

        this.control().setValue([...currentIds, value.value]);
    }

    protected toggle(): void {
        this.toggleShowAll.emit(!this.showAll());
    }
}
